import React from 'react'

import clsx from 'clsx'

import EmptyState from 'components/EmptyState'
import Icon from 'components/Icon'
import Tooltip from 'components/Tooltip'
import Typography from 'components/Typography'
import { useEventContext } from 'context/EventContext'
import { thousandSeparator } from 'helpers/formatters'

import starSVG from 'assets/empty-state-star.svg'

import classes from './MultiColorProgressBar.module.scss'

/** @type {import("./types").default} */
const MultiColorProgressBar = ({ reviews, listingId }) => {
  const { sendEvent } = useEventContext({
    name: 'engagement_hover',
  })

  const sendTooltipEvent = label => {
    sendEvent({
      name: 'engagement_hover',
      engagementDestination: 'show_reviews-sentiment-range',
      engagementDetails: label,
      engagementComponent: `product_reviews`,
    })
  }

  const negativeReviews = {
    name: '1-2',
    total: reviews.by_rating['1'] + reviews.by_rating['2'],
    value: ((reviews.by_rating['1'] + reviews.by_rating['2']) / reviews.total) * 100,
    hoverStarLabel: 'hover_1-2-stars',
    hoverTextLabel: 'text-link_1-2-stars',
    clickengagementDestination: 'go-to_reviews_1-2-stars',
    params: '?rating_rounded%5B%5D=1&rating_rounded%5B%5D=2',
  }
  const midReviews = {
    name: '3-4',
    total: reviews.by_rating['3'] + reviews.by_rating['4'],
    value: ((reviews.by_rating['3'] + reviews.by_rating['4']) / reviews.total) * 100,
    hoverStarLabel: 'hover_3-4-stars',
    hoverTextLabel: 'text-link_3-4-stars',
    clickengagementDestination: 'go-to_reviews_3-4-stars',
    params: '?rating_rounded%5B%5D=3&rating_rounded%5B%5D=4',
  }
  const positiveReviews = {
    name: '5',
    total: reviews.by_rating['5'],
    value: (reviews.by_rating['5'] / reviews.total) * 100,
    hoverStarLabel: 'hover_5-stars',
    hoverTextLabel: 'text-link_5-stars',
    clickengagementDestination: 'go-to_reviews_5-stars',
    params: '?rating_rounded%5B%5D=5',
  }

  const overallReviews = [negativeReviews, midReviews, positiveReviews]

  const bars =
    overallReviews.length &&
    overallReviews.map((item, index) => {
      if (item.value > 0) {
        return (
          <Tooltip
            enterTouchDelay={100}
            key={`bar_${listingId}_${item.name}`}
            className="CategoryLeadersLabel"
            title={`${Math.round(item.value) + '%'}`}
            placement="top"
            sendOnEnterEvents={() => sendTooltipEvent(item.hoverStarLabel)}
          >
            <div
              className={clsx(classes.bar, {
                [classes.barGreen]: item.name === '5',
                [classes.barYellow]: item.name === '3-4',
                [classes.barRed]: item.name === '1-2',
              })}
              id={`bar_${index + 1}`}
              style={{ width: item.value + '%' }}
            >
              <span>&nbsp;</span>
            </div>
          </Tooltip>
        )
      }
    })

  const legends =
    overallReviews.length &&
    overallReviews.map((item, index) => {
      if (item.value > 0) {
        return (
          <Tooltip
            enterTouchDelay={100}
            className="CategoryLeadersLabel"
            key={`legend_${listingId}_${item.name}`}
            title={`${Math.round(item.value) + '%'}`}
            placement="top"
            sendOnEnterEvents={() => sendTooltipEvent(item.hoverStarLabel)}
          >
            <div className={classes.legend} id={`legend_${index + 1}`}>
              <span className="label">
                <Icon
                  className={clsx({
                    [classes.starGreen]: item.name === '5',
                    [classes.starYellow]: item.name === '3-4',
                    [classes.starRed]: item.name === '1-2',
                  })}
                  icon="star"
                  size="16"
                />
                <Typography variant="tight-xs" component="span" className={classes.title}>
                  {item.name}
                </Typography>
                ({thousandSeparator(item.total)})
              </span>
            </div>
          </Tooltip>
        )
      }
    })

  return (
    <div className={clsx('MultiColorProgressBar', classes.root, reviews.total === 0 && classes.noProgressBar)}>
      {reviews.total > 0 ? (
        <>
          <div className={classes.bars} id={classes.barsContainer}>
            {bars == '' ? '' : bars}
          </div>
          <div className={classes.legends} id={classes.legendsContainer}>
            {legends == '' ? '' : legends}
          </div>
        </>
      ) : (
        <EmptyState img={starSVG.src} text="No reviews" />
      )}
    </div>
  )
}

export default MultiColorProgressBar
